import { GraduationCap, Scale, Globe2, BookMarked, ArrowUpRight } from "lucide-react";
import { SITE, IMAGES } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, MaskedLine, SectionHeading } from "../components/Reveal";

const CHAPTER_ICONS = [GraduationCap, Globe2, Scale];

const PUBLICATIONS = [
  {
    title: "Article 13 TFEU: Between symbolism and effectiveness, animal sentience in European constitutionalism (on the EU-Mercosur Free Trade Agreement)",
    source: "UNIO — EU Law Journal, 2026",
    url: "https://officialblogofunio.com/2026/03/06/article-13-tfeu-between-symbolism-and-effectiveness-animal-sentience-in-european-constitutionalism/",
  },
];

export default function AboutPage() {
  const { t } = useLanguage();
  const chapters = t("about.chapters");
  const timeline = t("about.timeline");

  return (
    <main data-testid="about-page" className="pt-32 pb-24">
      {/* INTRO */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-start mb-24 sm:mb-32">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-gold/60 pointer-events-none" />
              <div className="relative overflow-hidden aspect-[4/5] bg-sage">
                <img
                  src={IMAGES.portraitAlt}
                  alt={`${t("about.overline")} — ${SITE.name}`}
                  className="w-full h-full object-cover"
                  data-testid="about-portrait"
                />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757A70] mt-4">
                {t("site.oa")}
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pt-8">
          <p className="overline-label mb-6">{t("about.overline")}</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium tracking-tight text-ebony" data-testid="about-title">
            <MaskedLine delay={0.1}>{SITE.name}</MaskedLine>
          </h1>
          <Reveal delay={0.2}>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-olive mt-4 mb-10">
              {t("site.title")}
            </p>
            <div className="space-y-6 text-base text-[#4D5248] leading-relaxed max-w-2xl">
              <p>{t("about.bio1")}</p>
              <p>{t("about.bio2")}</p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* MANIFESTO — CAPÍTULOS NUMERADOS */}
      <section className="py-16 sm:py-24 bg-forestdark text-alabaster mb-24 sm:mb-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">{t("manifesto.overline")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight max-w-xl">
              {t("manifesto.title")}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-12 md:gap-8">
            {chapters.map((c, i) => {
              const Icon = CHAPTER_ICONS[i];
              return (
                <Reveal key={c.n} delay={0.1 * i}>
                  <div data-testid={`manifesto-chapter-${c.n}`} className="border-t border-[#3A3F38] pt-8 relative">
                    <span className="font-serif italic text-6xl text-[#2D4030] absolute -top-4 right-0 select-none">{c.n}</span>
                    <Icon size={22} strokeWidth={1.5} className="text-gold mb-6" />
                    <h3 className="font-serif text-xl font-medium text-alabaster mb-4">{c.title}</h3>
                    <p className="text-sm text-[#9AA096] leading-relaxed">{c.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <blockquote className="mt-20 border-l-2 border-gold pl-6 sm:pl-10 max-w-3xl">
              <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-alabaster">
                {t("about.quote")}
              </p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* PERCURSO */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 mb-24 sm:mb-32">
        <div className="lg:col-span-4">
          <SectionHeading overline={t("journey.overline")} title={t("journey.title")} />
          <Reveal delay={0.15}>
            <p className="text-sm text-[#4D5248] leading-relaxed mt-6 max-w-sm">
              {t("journey.intro")}
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <ol className="relative border-l border-[#E2E4DC] ml-2" data-testid="about-timeline">
            {timeline.map((tl, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <li className="relative pl-10 pb-10 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 bg-gold" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">{tl.year}</p>
                  <p className="font-serif text-lg text-ebony">{tl.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>

      {/* PUBLICAÇÕES */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading overline={t("pubs.overline")} title={t("pubs.title")} className="mb-14" />
        <div className="border-t border-[#E2E4DC]" data-testid="publications-list">
          {PUBLICATIONS.map((p, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <div className="group flex items-start gap-6 py-8 border-b border-[#E2E4DC]" data-testid={`publication-item-${i}`}>
                <BookMarked size={18} strokeWidth={1.5} className="text-olive mt-1.5 shrink-0" />
                <div>
                  <h3 className="font-serif text-lg sm:text-xl font-medium text-ebony leading-snug group-hover:text-forest transition-colors max-w-3xl">
                    {p.url ? (
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`publication-link-${i}`}
                        className="inline-flex items-start gap-2"
                      >
                        {p.title}
                        <ArrowUpRight size={15} className="text-gold shrink-0 mt-1.5" />
                      </a>
                    ) : (
                      p.title
                    )}
                  </h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9AA096] mt-3">{p.source}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
